import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, inr } from "../lib/api";
import { motion } from "framer-motion";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [email, setEmail] = useState(location.state?.email || "");
  const [error, setError] = useState("");

  const fetchOrder = (em) => {
    api.get(`/track/${orderId}?email=${encodeURIComponent(em)}`)
      .then((r) => setOrder(r.data.order))
      .catch(() => setError("We couldn't load this order. Use Track Order with your order ID and email."));
  };

  useEffect(() => { if (email) fetchOrder(email); }, []);

  if (!order) return (
    <div className="pt-40 pb-24 max-w-md mx-auto px-4 text-center min-h-screen" data-testid="order-confirm-loading">
      {error ? <p className="text-[#E63946]">{error}</p> : (
        <form onSubmit={(e) => { e.preventDefault(); fetchOrder(email); }} className="space-y-4">
          <p className="font-serif-lux text-2xl text-[#2E1A22]">Confirm your email to view this order</p>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used at checkout"
            className="w-full bg-[#FBF7F1] border border-[#7E1E3F]/25 rounded-full px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60" data-testid="order-confirm-email" />
          <button className="bg-[#7E1E3F] text-[#FFFFFF] text-xs font-semibold tracking-wider uppercase px-8 py-3 rounded-full" data-testid="order-confirm-submit">View Order</button>
        </form>
      )}
    </div>
  );

  return (
    <div className="pt-32 pb-24 max-w-3xl mx-auto px-4 sm:px-6" data-testid="order-confirmation">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="text-center">
        <CheckCircle2 className="mx-auto text-[#2A9D8F]" size={52} />
        <h1 className="font-serif-lux text-4xl text-[#2E1A22] mt-5">It's On Its Way</h1>
        <p className="text-[#857078] mt-3">Order <span className="gold-text font-mono-lux" data-testid="confirm-order-id">{order.order_id}</span> confirmed{order.payment?.status === "paid" ? " and paid" : ""}.</p>
        <p className="text-sm text-[#857078] mt-1">Estimated delivery: {order.estimated_delivery}</p>
      </motion.div>

      <div className="card-surface rounded-2xl p-6 mt-10 space-y-5">
        {order.items.map((i) => (
          <div key={i.slug + (i.personalization?.name || "")} className="flex gap-4 items-center">
            <img src={i.image} alt="" className="w-14 h-16 object-cover rounded-lg" />
            <div className="flex-1">
              <p className="text-sm text-[#45303A]">{i.name}</p>
              <p className="text-xs text-[#857078]">Qty {i.qty}</p>
              {i.personalization && (
                <p className="text-xs text-[#B4853B] mt-0.5 flex items-center gap-1.5" data-testid={`confirm-pers-${i.slug}`}>
                  {i.personalization.photo && <img src={i.personalization.photo} alt="" className="w-5 h-5 rounded object-cover" />}
                  Personalized for “{i.personalization.name}”{i.personalization.message ? ` — “${i.personalization.message}”` : ""}
                </p>
              )}
            </div>
            <span className="text-sm text-[#2E1A22]">{inr(i.line_total)}</span>
          </div>
        ))}
        <div className="border-t border-[#7E1E3F]/10 pt-4 space-y-1.5 text-sm">
          <div className="flex justify-between text-[#857078]"><span>Subtotal</span><span>{inr(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-[#2A9D8F]"><span>Discount {order.coupon && `(${order.coupon})`}</span><span>−{inr(order.discount)}</span></div>}
          {order.gift_wrap && <div className="flex justify-between text-[#857078]"><span>Premium Gift Wrapping</span><span>{inr(order.gift_wrap_fee)}</span></div>}
          <div className="flex justify-between text-[#2E1A22] font-semibold text-base pt-1"><span>Total Paid</span><span>{inr(order.total)}</span></div>
        </div>
        {order.gift_message && (
          <div className="bg-[#7E1E3F]/8 border border-[#7E1E3F]/20 rounded-xl p-4 text-sm">
            <p className="text-[#7E1E3F] text-xs font-mono-lux tracking-[0.2em] uppercase mb-1">Gift Message</p>
            <p className="text-[#45303A] italic">"{order.gift_message.message}"</p>
            <p className="text-[#857078] text-xs mt-1">To {order.gift_message.recipient} · From {order.gift_message.sender}</p>
          </div>
        )}
        <div className="text-sm text-[#857078]">
          <p className="text-xs font-mono-lux tracking-[0.2em] uppercase text-[#857078] mb-1">Delivering To</p>
          {order.address.name}, {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.city}, {order.address.state} – {order.address.pincode}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-4 mt-10">
        <Link to={`/track?id=${order.order_id}`} className="text-xs tracking-[0.15em] uppercase font-semibold bg-[#7E1E3F] text-[#FFFFFF] rounded-full px-8 py-3.5 hover:bg-[#5C1230] transition-colors" data-testid="confirm-track-button">Track Order</Link>
        <Link to="/gifts" className="text-xs tracking-[0.15em] uppercase font-medium border border-[#7E1E3F]/40 text-[#5C1230] rounded-full px-8 py-3.5 hover:bg-[#7E1E3F]/10 transition-colors" data-testid="confirm-continue-button">Continue Shopping</Link>
      </div>
    </div>
  );
}
